import { useEffect } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { LoanAmountFields } from "./LoanAmountFields";
import { useLoanForm } from "../hooks/useLoanForm";
import { loanPayload } from "../lib/loanForm";
import api, { formatApiError } from "../lib/api";

const BORROW_LOAN_FIELD_IDS = {
  amount: "loan-amount",
  amountTestId: "loan-amount-input",
  interest: "interest-amount",
  interestTestId: "interest-amount-input",
  deposit: "deposit-amount",
  depositTestId: "deposit-amount-input",
  stampDuty: "stamp-duty",
  stampDutyTestId: "stamp-duty-input",
  cashOnHandTestId: "cash-on-hand-value",
  outstandingTestId: "outstanding-value",
  loanDate: "loan-date",
  loanDateTestId: "loan-date-input",
  termDays: "term-days",
  termDaysTestId: "term-days-input",
  dueDate: "due-date",
  dueDateTestId: "next-due-date-input",
  dueDateLabel: "Next Due Date",
  remarks: "loan-remarks",
  remarksTestId: "loan-remarks-input",
};

export default function BorrowLoanDialog({ customer, onClose, onDone }) {
  const loanForm = useLoanForm();
  const { reset } = loanForm;

  useEffect(() => {
    if (customer) {
      reset();
    }
  }, [customer, reset]);

  const submit = async (event) => {
    event.preventDefault();
    loanForm.setSubmitted(true);
    if (!loanForm.isComplete()) {
      toast.error("Please complete every loan field before creating the loan.");
      return;
    }
    loanForm.setSaving(true);
    try {
      await api.post(`/customers/${customer.id}/loans`, loanPayload(loanForm.form));
      toast.success("New customer loan created");
      onDone();
      onClose();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      loanForm.setSaving(false);
    }
  };

  return (
    <Dialog open={Boolean(customer)} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="borrow-loan-dialog" data-testid="borrow-loan-dialog">
        <DialogHeader>
          <DialogTitle>New Loan — {customer?.full_name}</DialogTitle>
          <DialogDescription>
            Create a loan directly from this customer’s record.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="borrow-loan-form" noValidate>
          <div className="borrow-loan-heading">
            <strong>New Loan</strong>
            <span>Linked to {customer?.full_name}</span>
          </div>
          <LoanAmountFields
            calculations={loanForm.calculations}
            form={loanForm.form}
            ids={BORROW_LOAN_FIELD_IDS}
            isInvalid={loanForm.isInvalid}
            onChange={loanForm.update}
          />
          <div className="borrow-loan-actions">
            <Button
              type="submit"
              disabled={loanForm.saving}
              data-testid="create-customer-loan-button"
            >
              {loanForm.saving ? "Creating…" : "Create Loan"}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              data-testid="cancel-customer-loan-button"
            >
              Cancel
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}